import { ControlNumberField, ControlledChoice } from 'components/Form';
import type { WorkOrderCostLimitDistributionData, WorkOrderChangeCostLimitDistributionData } from 'gql/graphql';
import { Text } from 'uikit';

import { useMemo } from 'react';
import type { Control } from 'react-hook-form';
import { useWatch } from 'react-hook-form';

import { replacerCost } from '../../utils/replacerCost';

import { limitsChoiceItems } from './commonData';

import Styles from './EditOtherDistribution.module.css';

export const EditOtherDistributionLimits = ({
    control,
    workOrderLimitsData,
    isCalculating,
    isWorkOrderChange,
    isVisible,
    costRounding,
}: {
    control: Control<any>;
    workOrderLimitsData?: WorkOrderCostLimitDistributionData[] | WorkOrderChangeCostLimitDistributionData[] | null;
    isCalculating?: boolean;
    isWorkOrderChange: boolean;
    isVisible: boolean;
    costRounding: number;
}) => {
    const { includedCost = {}, isIncluded = {} } = useWatch({
        control,
    });

    const includedCostLabel = useMemo(
        () => (isWorkOrderChange ? 'Включить в Изм. к НЗ' : 'Включить в НЗ'),
        [isWorkOrderChange],
    );

    const isFormWithNoCheckedLimits = useMemo(() => {
        if (isCalculating) {
            return Object.values(includedCost as Record<string, string | number | null | undefined>).every(
                (value) => !value || replacerCost(value) === 0,
            );
        }

        return Object.values(
            isIncluded as Record<
                string,
                {
                    isIncluded: boolean;
                }
            >,
        ).every((value) => !value.isIncluded);
    }, [includedCost, isCalculating, isIncluded]);

    const validateField = () => {
        if (isFormWithNoCheckedLimits) {
            return 'Обязательное поле';
        }

        return true;
    };

    return (
        <div>
            {workOrderLimitsData?.map((limitItem) => (
                <div key={limitItem?.season?.id} className={Styles.seasonBlock}>
                    <Text className={Styles.seasonTitle}>Сезон {limitItem?.season?.year}</Text>
                    <ControlNumberField
                        name={`costLimit.${limitItem?.season?.id}`}
                        label="Лимит по Договору"
                        control={control}
                        type="number"
                        labelPosition="left"
                        width="full"
                        size="s"
                        disabled
                        className={Styles.seasonItem}
                    />
                    <ControlNumberField
                        name={`totalRemainderCost.${limitItem?.season?.id}`}
                        label="Остаток"
                        control={control}
                        type="number"
                        labelPosition="left"
                        width="full"
                        size="s"
                        disabled
                        className={Styles.seasonItem}
                    />
                    {isCalculating ? (
                        <ControlNumberField
                            name={`includedCost.${limitItem?.season?.id}`}
                            label={includedCostLabel}
                            control={control}
                            type="number"
                            labelPosition="left"
                            width="full"
                            size="s"
                            className={Styles.seasonItem}
                            isTooltipHidden={!isVisible}
                            required
                            rules={{
                                validate: validateField,
                            }}
                            customStaticTooltip
                            decimalScale={costRounding}
                        />
                    ) : (
                        <div
                            className={`TextField TextField_labelPosition_left TextField_size_s TextField_view_default TextField_width_full ${Styles.marginTBlock}`}
                        >
                            <div className="Text Text_lineHeight_m Text_size_s Text_view_secondary FieldLabel TextField-Label TextField-Label_labelPosition_left">
                                {includedCostLabel}
                            </div>
                            <ControlledChoice
                                control={control}
                                items={limitsChoiceItems}
                                name={`isIncluded.${limitItem?.season?.id}`}
                                getItemLabel={({ label }) => label}
                                size="s"
                                width="full"
                                textLabelClassName={Styles.marginBlock}
                                isTooltipHidden={!isVisible}
                                required
                                rules={{
                                    validate: validateField,
                                }}
                                key={limitItem?.season?.id}
                                direction="downStartLeft"
                                customStaticTooltip
                                classChoiceGroup={Styles.withoutRightMargin}
                                view="ghost"
                            />
                        </div>
                    )}
                </div>
            ))}
        </div>
    );
};

EditOtherDistributionLimits.displayName = 'EditOtherDistributionLimits';
